import { supabase } from "../../../lib/supabase";

/**
 * Lectura y escritura del design system de la marca activa, contra
 * `brand_profiles` — la MISMA tabla y los MISMOS campos (`colors`, `fonts`,
 * `context`, más `name`/`rubro`/`logo_url` para el tab Perfil) que ya edita
 * `config_sections/CompanyData`. Es deliberado: esta pantalla y "Mi marca" →
 * "Mi diseño" tienen que mostrar exactamente el mismo registro, sin inventar
 * una segunda forma de leerlo ni de guardarlo.
 *
 * ESCRITURA — tres reglas que no se negocian (ADR-2026-08-16-marca-page-escritura):
 *
 *  1. Solo se escriben `colors`, `fonts` y `context`. `name`, `rubro` y
 *     `logo_url` NO se tocan desde acá: en `CompanyData` esos tres tienen
 *     efectos CRUZADOS sobre `company_profiles` (se espejan cuando la marca es
 *     `is_default`, y el rubro además pasa por `mergeLastCrearAnuncioState`).
 *     Replicar eso a medias dejaría a las dos pantallas mostrando cosas
 *     distintas — que es exactamente el problema que este módulo evita.
 *
 *  2. La normalización es la MISMA que la de `CompanyData.tsx`, campo por
 *     campo (ver el detalle en cada función). Si las dos pantallas guardaran
 *     el mismo dato con forma distinta habría dos fuentes de verdad.
 *
 *  3. Ninguna de estas funciones tira: devuelven `{ ok, error }` con el error
 *     ya redactado para mostrarle al usuario. Lo técnico va a `console.error`.
 *     (`leerMarca`, en cambio, SÍ tira — ver su doc: ahí la excepción es lo
 *     que le permite a la pantalla distinguir "sin datos" de "falló".)
 */

export interface ColorMarca {
  hex: string;
  name?: string;
  role?: string;
}

/**
 * Forma real de `brand_profiles.fonts`, verificada contra el uso que ya hace
 * `CompanyData.tsx` (`displayFonts.suggested` es un array de familias, no un
 * string suelto — `Record<string, string>` no la representa y hubiera hecho
 * que "tipografías por rol" nunca tuviera nada que mostrar).
 */
export interface FontsMarca {
  /** Descripción en prosa del estilo tipográfico (ej. "Serif elegante, peso regular…"). */
  style?: string;
  /** Familias sugeridas, en orden: [0] = principal, [1] = secundaria. */
  suggested?: string[];
}

export interface MarcaDS {
  id: string;
  name: string | null;
  rubro: string | null;
  logo_url: string | null;
  context: string | null;
  colors: ColorMarca[];
  fonts: FontsMarca | null;
}

/**
 * Trae el design system de una marca por id.
 *
 * Devuelve `null` SOLO cuando la fila no existe (id inválido, marca
 * borrada). Ante un error real de la consulta (red, RLS, timeout) TIRA la
 * excepción en vez de devolver `null` — si los dos casos se confundieran, la
 * pantalla no podría distinguir "esta marca todavía no tiene datos"
 * (legítimo, hay que decirlo tal cual) de "la consulta falló" (hay que
 * avisar que falló, no mentir con un vacío).
 */
export async function leerMarca(brandId: string): Promise<MarcaDS | null> {
  const { data, error } = await supabase
    .from("brand_profiles")
    .select("id, name, rubro, logo_url, context, colors, fonts")
    .eq("id", brandId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    id: data.id,
    name: data.name ?? null,
    rubro: data.rubro ?? null,
    logo_url: data.logo_url ?? null,
    context: data.context ?? null,
    colors: Array.isArray(data.colors) ? (data.colors as ColorMarca[]) : [],
    fonts: (data.fonts as FontsMarca | null) ?? null,
  };
}

/** Resultado de toda escritura de esta pantalla: nunca una excepción. */
export interface ResultadoEscritura {
  ok: boolean;
  /** Mensaje ya redactado para mostrarle al usuario, o `null` si salió bien. */
  error: string | null;
}

/**
 * Criterio de hex válido: EL MISMO regex que `handleSaveColors` de
 * `CompanyData.tsx` (`/^#([0-9a-f]{3}|[0-9a-f]{6})$/` sobre el hex ya
 * `trim()`+`toLowerCase()`). Se exporta para que la UI valide con el mismo
 * criterio con el que después va a guardar, en vez de tener dos ideas
 * distintas de qué es un color.
 *
 * Devuelve el hex normalizado, o `null` si no es válido.
 */
export function normalizarHex(hex: string | null | undefined): string | null {
  const limpio = (hex ?? "").trim().toLowerCase();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(limpio) ? limpio : null;
}

/**
 * Guarda la paleta en `brand_profiles.colors`.
 *
 * Shape escrito: array de `{ hex, name?, role? }` con el hex ya normalizado
 * (`trim` + minúsculas) y sin claves extra — idéntico al que arma
 * `handleSaveColors` en `CompanyData.tsx`. Las claves `name`/`role` solo se
 * incluyen si venían: un color nuevo se guarda como `{ "hex": "#888888" }`
 * pelado, igual que allá.
 *
 * DIFERENCIA DELIBERADA con `CompanyData`: allá los hex inválidos se
 * DESCARTAN en silencio (`.filter(...)`) y el color desaparece sin aviso. Acá,
 * si hay UNO inválido, no se escribe la paleta y se devuelve el error: borrarle
 * un color al usuario sin decírselo es peor que no guardar.
 *
 * OJO con el alcance de ese freno: para en seco `colors` y NADA MÁS. No es una
 * validación de tanda. Hay marcas reales cuya paleta llegó del onboarding con
 * hex sin `#` (`FFFFFF`), con `hex: null` o con strings sueltos en vez de
 * objetos; si este `return` bloqueara también a `fonts` o a `context`, esos
 * usuarios no podrían guardar NADA en la pantalla hasta arreglar a mano colores
 * que nunca pusieron — y en "Mi diseño" la misma marca guarda sin problema. Por
 * eso quien llama solo intenta esta escritura si el usuario tocó la paleta (ver
 * `guardar` en `MarcaVisualPanel`).
 *
 * El criterio de validez y la normalización siguen siendo los mismos que los de
 * `CompanyData`; lo único distinto es qué se hace con lo inválido.
 */
export async function guardarColores(
  brandId: string,
  colors: ColorMarca[],
): Promise<ResultadoEscritura> {
  const normalizados: ColorMarca[] = [];
  const invalidos: string[] = [];
  for (const color of colors) {
    // `color?.` a propósito: la lista viene de un JSON de la base, así que el
    // tipo es una promesa, no una garantía.
    const hex = normalizarHex(color?.hex);
    if (!hex) {
      invalidos.push((color?.hex ?? "").trim() || "(vacío)");
      continue;
    }
    normalizados.push({
      hex,
      ...(color.name !== undefined ? { name: color.name } : {}),
      ...(color.role !== undefined ? { role: color.role } : {}),
    });
  }
  if (invalidos.length > 0) {
    return {
      ok: false,
      error: `Los colores no se guardaron: ${invalidos.join(", ")} no ${
        invalidos.length === 1 ? "es un color válido" : "son colores válidos"
      }. Usá el formato #RRGGBB.`,
    };
  }
  try {
    const { error } = await supabase
      .from("brand_profiles")
      .update({ colors: normalizados })
      .eq("id", brandId);
    if (error) throw error;
    return { ok: true, error: null };
  } catch (e) {
    console.error("[marcaApi] error guardando colores", e);
    return { ok: false, error: "No se pudieron guardar los colores. Probá de nuevo." };
  }
}

/**
 * Guarda las tipografías en `brand_profiles.fonts`.
 *
 * Shape escrito: el objeto `fonts` COMPLETO que se le pasa, con `suggested`
 * normalizado (`trim` + se descartan los vacíos) — igual que
 * `handleSaveFonts` en `CompanyData.tsx`, que hace
 * `{ ...(activeBrand.fonts || {}), suggested: cleaned }`.
 *
 * Por eso el parámetro es el objeto entero y no solo las familias: quien llama
 * tiene que pasar `{ ...(marca.fonts ?? {}), suggested: [...] }` para no
 * perder `style` (la descripción en prosa que escribió la IA) ni cualquier
 * otra clave que la fila ya tuviera. Un `update({ fonts: { suggested } })`
 * pelado la borraría, y "Mi diseño" dejaría de mostrar el estilo.
 */
export async function guardarFuentes(
  brandId: string,
  fonts: FontsMarca,
): Promise<ResultadoEscritura> {
  const suggested = Array.isArray(fonts.suggested)
    ? fonts.suggested.map((f) => (f ?? "").trim()).filter(Boolean)
    : [];
  try {
    const { error } = await supabase
      .from("brand_profiles")
      .update({ fonts: { ...fonts, suggested } })
      .eq("id", brandId);
    if (error) throw error;
    return { ok: true, error: null };
  } catch (e) {
    console.error("[marcaApi] error guardando tipografías", e);
    return { ok: false, error: "No se pudieron guardar las tipografías. Probá de nuevo." };
  }
}

/**
 * Guarda el contexto ("memoria de la IA") en `brand_profiles.context`.
 *
 * Shape escrito: el string TAL CUAL, sin `trim` — igual que
 * `handleSaveMemoria` en `CompanyData.tsx` (`update({ context: memoriaInput })`).
 * No se recorta ni se convierte "" en `null` a propósito: `CompanyData`
 * tampoco lo hace, y ambas pantallas leen el campo con `context || …`, así que
 * "" y `null` se ven igual. Cambiarlo de un solo lado sería justamente crear
 * la segunda forma de guardar lo mismo que hay que evitar.
 */
export async function guardarContexto(
  brandId: string,
  context: string | null,
): Promise<ResultadoEscritura> {
  try {
    const { error } = await supabase
      .from("brand_profiles")
      .update({ context })
      .eq("id", brandId);
    if (error) throw error;
    return { ok: true, error: null };
  } catch (e) {
    console.error("[marcaApi] error guardando contexto", e);
    return { ok: false, error: "No se pudo guardar el contexto. Probá de nuevo." };
  }
}
